/**
 * SYNC NOTE: This file must be kept in sync with the public changelog page at
 * website/src/docs/changelog.njk
 *
 * When adding a new release, update BOTH files.
 */

export type ChangeCategory = 'feature' | 'improvement' | 'fix';

/**
 * A single changelog change.
 *
 * Entries carry a `summary` of one sentence (two at most, and only for a big
 * change) plus a `docsUrl` / `blogUrl` that holds the detail. The optional
 * markdown `details` body (shown behind "Show more info…" in the app and
 * below the summary on the web) is legacy from 1.4.10–1.4.25: detail now lives
 * in the linked docs or blog post, not in the changelog.
 *
 * Legacy entries (releases predating the two-level format) carry a single
 * `description` paragraph; the renderer falls back to it when `summary` is absent.
 */
export type ChangelogChange = {
  category: ChangeCategory;
  /** Stable kebab-case slug, unique within the version. Used for analytics + web anchors. */
  id?: string;
  /** 1–2 sentence short text. Shown in the app list and as the web lead paragraph. */
  summary?: string;
  /** Full markdown detail (short intro + bullets). When present, a "Show more info…" link is shown. */
  details?: string;
  /** Absolute jamsql.com docs URL, if a docs page covers this change. */
  docsUrl?: string;
  /** Absolute jamsql.com blog URL, if an announcement post exists. */
  blogUrl?: string;
  /** Legacy single-paragraph text (releases before the two-level format). */
  description?: string;
};

/** The text shown in the changelog list for a change (two-level summary, else legacy description). */
export function changeText(change: ChangelogChange): string {
  return change.summary ?? change.description ?? '';
}

export type ChangelogEntry = {
  version: string;
  date: string;
  highlights?: string;
  changes: ChangelogChange[];
};

/** Changelog entries, newest first */
export const changelog: ChangelogEntry[] = [
  {
    version: '1.5.3',
    date: '2026-09-26',
    highlights: 'Azure Storage connections: Blob storage, Data Lake (ADLS Gen2), File Shares, Queues and Tables in the same app as your SQL databases.',
    changes: [
      {
        category: 'feature',
        id: 'azure-storage',
        summary:
          'Azure Storage \u2014 connect to a storage account, a SAS-scoped resource or the Azurite emulator to work with blobs, Data Lake (ADLS Gen2) directories and access control, SMB and NFS file shares, queues and Table entities, and to preview and edit files in a pane next to the list.',
        docsUrl: 'https://jamsql.com/docs/azure-storage/',
        blogUrl: 'https://jamsql.com/alternatives/azure-storage-explorer/',
      },
      {
        category: 'feature',
        id: 'visual-query-editor-on-by-default',
        summary:
          'Visual Query Editor \u2014 now on by default (still in Beta): switch a Query tab between SQL and Visual to read and edit the statement under the cursor as clause cards, or edit it in place from the gutter pencil.',
        docsUrl: 'https://jamsql.com/docs/visual-query-editor/',
        blogUrl: 'https://jamsql.com/blog/2026-09-26-visual-query-editor/',
      },
      {
        category: 'feature',
        id: 'filter-in-list-and-vqe-filter-surfaces',
        summary:
          'Filters \u2014 "in list" and "not in list" operators in Table Explorer, the Visual Query Editor, Data Compare and MCP.',
        docsUrl: 'https://jamsql.com/docs/visual-query-editor/#where-having',
      },
      {
        category: 'improvement',
        id: 'new-connection-dialog',
        summary:
          'New Connection \u2014 the dialog opens on a list of engines you can filter, with Paste connection string, Browse Azure, Detect local databases and Import from another tool beside it. A pasted connection string shows what it will fill before you continue, and Import and Detect open on top of the dialog without losing what you typed.',
        docsUrl: 'https://jamsql.com/docs/connections/#creating-a-connection',
      },
      {
        category: 'improvement',
        id: 'update-ready-prompt-dismiss',
        summary:
          'Updates \u2014 the "Update ready" prompt can be closed, and the downloaded update installs the next time you quit the app.',
        docsUrl: 'https://jamsql.com/docs/getting-started/',
      },
      {
        category: 'fix',
        id: 'rows-affected-total',
        summary: 'Query Editor \u2014 multi-statement DML reports the total rows affected.',
        docsUrl: 'https://jamsql.com/docs/query-editor/#rows-affected',
      },
    ],
  },
  {
    version: '1.5.2',
    date: '2026-09-24',
    changes: [
      {
        category: 'fix',
        id: 'linux-arch-and-older-distros',
        summary:
          'Linux \u2014 the Arch Linux package installs on current Arch and Arch-based distributions, and the app starts on distributions back to Ubuntu 20.04 and Debian 11.',
        docsUrl: 'https://jamsql.com/download/linux/',
      },
    ],
  },
  {
    version: '1.5.1',
    date: '2026-09-21',
    highlights:
      "Database pickers open with the list the Object Explorer already loaded, and say so plainly when a listing fails instead of offering guessed system database names.",
    changes: [
      {
        category: 'feature',
        id: 'editable-keybindings-execute-foundation',
        summary:
          'Keyboard shortcuts — Cmd/Ctrl+Enter and Shift+Enter execute the selection or the statement under the cursor.',
        docsUrl: 'https://jamsql.com/docs/keyboard-shortcuts/#query-editor',
      },
      {
        category: 'feature',
        id: 'editable-keybindings-settings-tab',
        summary:
          'Keyboard shortcuts — change, add, remove or reset shortcuts in Settings → Keyboard Shortcuts, and take them to another machine with Settings → Backup & Transfer.',
        docsUrl: 'https://jamsql.com/docs/keyboard-shortcuts/#customising-shortcuts',
      },
      {
        category: 'feature',
        id: 'editable-keybindings-schemes',
        summary:
          'Keyboard shortcuts — pick the SSMS / Azure Data Studio, JetBrains DataGrip, DBeaver or VS Code mssql extension scheme in Settings → Keyboard Shortcuts to use that tool\'s keys for executing, cancelling, formatting and showing results.',
        docsUrl: 'https://jamsql.com/docs/keyboard-shortcuts/#shortcut-schemes',
      },
      {
        category: 'improvement',
        id: 'database-picker-instant-list',
        summary:
          "Database pickers — every database dropdown now opens with the list the Object Explorer already loaded, and explains the failure instead of offering guessed system database names when the list can't be read.",
        docsUrl: 'https://jamsql.com/docs/query-editor/#database-switcher',
      },
      {
        category: 'fix',
        id: 'print-output',
        summary:
          'Query Editor \u2014 SQL Server PRINT and low-severity RAISERROR text, and PostgreSQL RAISE NOTICE / INFO / WARNING text, now appear in Messages instead of being dropped, and a script that prints alongside its results gets an Output view that keeps them in the order the server produced them.',
        docsUrl: 'https://jamsql.com/docs/query-editor/#print-and-raise-output',
      },
      {
        category: 'fix',
        id: 'suggest-popup-on-tab-open',
        summary:
          'Query Editor \u2014 the suggestion popup no longer opens on its own in a freshly opened tab, or after a reconnect, database switch or IntelliSense refresh, when the schema finishes loading, and a list you opened before it loaded is refreshed in place.',
        docsUrl: 'https://jamsql.com/docs/query-editor/#intellisense-and-autocomplete',
      },
      {
        category: 'fix',
        id: 'typing-lag-and-restore-freeze',
        summary:
          'Performance and stability — typing in the SQL editor no longer lags while autocomplete prepares suggestions, most noticeably on MySQL and large schemas, and resuming a session whose connection is already open no longer freezes the window.',
      },
    ],
  },
  {
    version: '1.5.0',
    date: '2026-09-19',
    highlights:
      'MetaInfo-driven autocomplete, a rebuilt Table Designer, and faster connects — get JSON-path / enum / loose-FK suggestions in the SQL editor, edit constraints, indexes, storage and partitioning from a single CREATE/ALTER TABLE buffer, and open connections without waiting on metadata.',
    changes: [
      {
        category: 'improvement',
        id: 'numeric-grouping',
        summary:
          'Results grids — a new Thousands separators setting (Settings → UI, or the right-click Number display submenu) renders numeric columns with locale separators, thin spaces or none.',
        docsUrl: 'https://jamsql.com/docs/query-editor/#numeric-display',
      },
      {
        category: 'improvement',
        id: 'ui-scale',
        summary:
          'Settings → UI — a new Interface scale control draws the whole app at 80–125%, and Windows now starts at 90%.',
        docsUrl: 'https://jamsql.com/docs/getting-started/#interface-scale',
      },
      {
        category: 'feature',
        id: 'refresh-intellisense',
        summary:
          'Query Editor — Cmd/Ctrl+Shift+R, or Refresh on a database in the Object Explorer, re-reads the schema behind autocomplete so objects created by another client complete without a reconnect. Rollback transaction moved to Cmd/Ctrl+Shift+X.',
        docsUrl: 'https://jamsql.com/docs/query-editor/#refresh-intellisense',
      },
      {
        category: 'improvement',
        id: 'autocomplete-off-switch',
        summary:
          'Query Editor — a new Suggest while typing switch (Settings → Advanced → SQL autocomplete) keeps the suggestion popup closed until you press Ctrl+Space.',
        docsUrl: 'https://jamsql.com/docs/query-editor/#turn-off-suggestions',
      },
      {
        category: 'improvement',
        id: 'intellisense-master-switch',
        summary:
          'Query Editor — a new Enable IntelliSense switch (Settings → Advanced → SQL autocomplete) turns off autocomplete, hovers, signature help and live squiggles at once.',
        docsUrl: 'https://jamsql.com/docs/query-editor/#disable-intellisense',
      },
      {
        category: 'feature',
        id: 'type-aware-query-editor',
        summary:
          'Query Editor — inline type-mismatch warnings on comparisons such as WHERE total = \'free\', plus hover-for-type on columns and function calls, both under Settings → Advanced → SQL autocomplete.',
        docsUrl: 'https://jamsql.com/docs/query-editor/',
      },
      {
        category: 'feature',
        id: 'metainfo-autocomplete',
        summary:
          'Autocomplete — the SQL editor now suggests JSON paths, enum values and loose-FK joins from your MetaInfo declarations, and Schema Compare diffs JSON classifications.',
      },
      {
        category: 'feature',
        id: 'table-designer-buffer',
        summary:
          'Table Designer — constraints, indexes, storage and partitioning, and identity, generated and collated columns are now all edited from a single CREATE/ALTER TABLE buffer with a View SQL toggle.',
        docsUrl: 'https://jamsql.com/docs/table-designer/',
      },
      {
        category: 'improvement',
        id: 'find-references-procedural',
        summary:
          'Find References — now also finds hits inside stored procedures and triggers on MSSQL, PostgreSQL, MySQL and Oracle.',
        docsUrl: 'https://jamsql.com/docs/query-editor/#go-to-definition-find-references-rename',
      },
      {
        category: 'improvement',
        id: 'peek-references-cross-object',
        summary:
          'Peek References — Shift+F12 previews every referencing view, routine, trigger and constraint body inline, and the grouped list moved to a Find All References in Tab action.',
        docsUrl: 'https://jamsql.com/docs/query-editor/#go-to-definition-find-references-rename',
      },
      {
        category: 'improvement',
        id: 'rename-buffer-aware',
        summary:
          'Rename Symbol — F2 renames table aliases and CTE names in the buffer, and a table, view or column rename updates references in open query tabs on the same database.',
        docsUrl: 'https://jamsql.com/docs/query-editor/#go-to-definition-find-references-rename',
      },
      {
        category: 'fix',
        id: 'query-editor-execution-fixes',
        summary:
          'Query Editor — multi-statement scripts now execute correctly on every engine, including MySQL DELIMITER routines, SQLite triggers, Oracle PL/SQL blocks with RETURNING … INTO, and MSSQL GO batches.',
      },
      {
        category: 'improvement',
        id: 'autocomplete-behavior-and-perf',
        summary:
          'Autocomplete — the start of a WHERE, HAVING or ON clause now suggests columns and boolean functions instead of table names, routine completion is much faster on large scripts, and Enter accepts a suggestion only when it changes what you typed.',
      },
      {
        category: 'improvement',
        id: 'autocomplete-position-coverage',
        summary:
          'Autocomplete — DDL, trigger, data-type, window, RETURNING and many other positions now offer exactly what fits, objects created earlier in the same script complete before a refresh, and after a GROUP BY only grouped columns, aggregates and aliases are offered. On large databases the schema loads in tiers so table names are ready within seconds, and the References index builds in the background without freezing the editor.',
        docsUrl: 'https://jamsql.com/docs/query-editor/#intellisense-and-autocomplete',
      },
      {
        category: 'improvement',
        id: 'autocomplete-ranking-tie-breakers',
        summary:
          'Autocomplete — within a tier, the table you are editing and your recently accepted completions float to the top, names already written in the current clause stop leading, and CREATE … name slots list schemas first.',
        docsUrl: 'https://jamsql.com/docs/query-editor/#how-suggestions-are-ranked',
      },
      {
        category: 'improvement',
        id: 'mssql-in-house-intellisense',
        summary:
          'SQL Server — IntelliSense and query execution now run fully in-house, and the bundled SQL Tools Service no longer launches on macOS or Linux.',
      },
      {
        category: 'fix',
        id: 'schema-ddl-reading-fixes',
        summary:
          'Schema Compare, Table Designer and Database Blueprint — PostgreSQL extension-owned objects stay out of the diff, and table DDL with inline comments, trailing CREATE INDEX statements or attributes the designer cannot write back is read without dropping or corrupting columns.',
        docsUrl: 'https://jamsql.com/docs/schema-compare/',
      },
      {
        category: 'feature',
        id: 'pgq-editor-support',
        summary:
          'Query Editor — SQL/PGQ property graph support (beta) for Oracle 23ai+ and PostgreSQL 19, with GRAPH_TABLE syntax and schema-aware completions.',
        docsUrl: 'https://jamsql.com/docs/query-editor/',
      },
      {
        category: 'fix',
        id: 'crash-dialog-frozen-ui',
        summary:
          'Stability — fixed full-UI freezes after closing the crash report dialog and while saving a connection with a slow or locked macOS keychain.',
      },
      {
        category: 'fix',
        id: 'windows-sql-server-connection-fixes',
        summary:
          'Windows — SQL Server connections are more reliable: an interrupted SQL Tools Service install repairs itself, and a local instance with an auto-detected port connects over Shared Memory or Named Pipes.',
      },
      {
        category: 'feature',
        id: 'blueprint-datacache-rebuild',
        summary:
          'Database Blueprint — a new Rebuild from .data.sql action on the missing-sidecar banner rebuilds data sidecars offline.',
        docsUrl: 'https://jamsql.com/docs/database-blueprint/',
      },
      {
        category: 'feature',
        id: 'live-syntax-squiggles-mysql-oracle-sqlite',
        summary:
          'Query Editor — live syntax-error squiggles for MySQL, Oracle, and SQLite.',
        docsUrl: 'https://jamsql.com/docs/query-editor/',
      },
      {
        category: 'feature',
        id: 'beta-features-opt-in',
        summary:
          'Settings → Beta Features — every beta feature is now a checkbox, so Database Blueprint, Cross-Engine Migration, Data Import, the PL/SQL Debugger, Agent Jobs and Package Explorer can be switched off.',
        docsUrl: 'https://jamsql.com/docs/getting-started/#beta-features',
      },
      {
        category: 'improvement',
        id: 'faster-connect',
        summary:
          'Connecting is faster on every engine: validation is a single round trip and the Object Explorer loads the connection\'s own database first.',
        docsUrl: 'https://jamsql.com/docs/connections/',
      },
      {
        category: 'fix',
        id: 'find-references-rename-fixes',
        summary:
          'Find References and Rename — fixes for multi-database SQL Server and PostgreSQL connections, case-sensitive databases, and rewrites that lost schema qualifiers or aliases in dependent bodies.',
        docsUrl: 'https://jamsql.com/docs/query-editor/#go-to-definition-find-references-rename',
      },
      {
        category: 'fix',
        id: 'oracle-table-explorer-clob-multibyte',
        summary:
          'Oracle — tables with CLOB or NCLOB columns holding multibyte text open in Table Explorer again, and both grids show the whole value instead of a 4000-character preview.',
        docsUrl: 'https://jamsql.com/docs/table-explorer/',
      },
      {
        category: 'improvement',
        id: 'notebook-text-view',
        summary:
          'SQL Notebooks — a Results / Text toggle above each cell\'s output shows the rows as a plain text table with a Copy button.',
        docsUrl: 'https://jamsql.com/docs/sql-notebooks/',
      },
    ],
  },
  {
    version: '1.4.26',
    date: '2026-09-06',
    changes: [
      {
        category: 'feature',
        id: 'deadlock-analyzer',
        summary:
          'Deadlock Analyzer — open or paste a SQL Server deadlock report (.xdl / XML), or read the deadlocks retained in a connection\'s system_health session, and inspect the victim, processes, lock modes and owner/waiter graph. Reports are parsed locally and never uploaded.',
        docsUrl: 'https://jamsql.com/docs/deadlock-analyzer/',
      },
      {
        category: 'feature',
        id: 'query-store-workspace',
        summary:
          'SQL Server — a new read-only Query Store workspace (right-click a database › Query Store…) compares two periods with execution-weighted averages, ranks candidate regressions and opens their plans in plan compare.',
        docsUrl: 'https://jamsql.com/docs/query-store/',
      },
      {
        category: 'fix',
        id: 'query-editor-stability-fixes',
        summary:
          'Query Editor — very large scripts no longer crash the app, and scrolling with the inline Execute / Peek bar no longer freezes it.',
        docsUrl: 'https://jamsql.com/docs/query-editor/#file-operations',
      },
      {
        category: 'fix',
        id: 'sql-server-results-and-sql-tools-service-fixes',
        summary:
          'SQL Server — the first results page is no longer cut short, and connections via SQL Tools Service no longer re-send failed batches or fail with "invalid OwnerUri" after a service restart.',
      },
      {
        category: 'improvement',
        id: 'ai-declare-json-columns',
        summary:
          'AI Integrations — connected agents can now declare a column as JSON alongside enum, loose-FK and reference-table declarations, still gated by Allow AI to declare MetaInfo.',
        docsUrl: 'https://jamsql.com/docs/ai-integrations-mcp/#declare-metainfo',
      },
      {
        category: 'feature',
        id: 'editor-color-schemes',
        summary:
          'Settings → UI → Appearance — a new Editor colors dialog offers five syntax-highlighting schemes plus a Custom one, with live previews, and applies the choice to every open SQL editor.',
        docsUrl: 'https://jamsql.com/docs/query-editor/#editor-colors',
      },
      {
        category: 'improvement',
        id: 'connection-error-editor-fixes',
        summary:
          'Connections — a failed connect now offers a one-click next step, Update user & password… for a failed sign-in or Edit connection… for an unreachable server.',
        docsUrl: 'https://jamsql.com/docs/connections/',
      },
      {
        category: 'fix',
        id: 'oracle-connection-lease',
        summary:
          'Oracle — a cancelled or abandoned query no longer poisons its connection, so the next query cannot fail with ORA-01013, and a busy connection pool no longer freezes the app.',
      },
      {
        category: 'improvement',
        id: 'stop-escalates-on-second-press',
        summary:
          'Query Editor and Table Explorer — pressing Stop again while it shows Stopping… forces the stop, which makes Stop work on an Oracle statement waiting behind another session\'s lock.',
        docsUrl: 'https://jamsql.com/docs/query-editor/#cancelling-queries',
      },
      {
        category: 'fix',
        id: 'connection-lifecycle-fixes',
        summary:
          'Connections — reconnecting ends an open transaction visibly instead of silently switching to auto-commit, Test Connection and Load databases use saved SSH secrets, and deleting a connection mid-connect can no longer let it reappear.',
        docsUrl: 'https://jamsql.com/docs/connections/',
      },
      {
        category: 'improvement',
        id: 'disconnect-cancels-running-queries',
        summary:
          'Connections — disconnecting, reconnecting, deleting or re-dialling a connection now cancels its running queries first on every engine, and releases notebook and transaction sessions held on it.',
        docsUrl: 'https://jamsql.com/docs/connections/#disconnecting',
      },
      {
        category: 'improvement',
        id: 'reconnect-on-every-engine',
        summary:
          'Connections — Reconnect is now in the right-click menu of every connection, not just SQL Server, and rebuilds the connection after a network change, VPN reconnect, laptop sleep or expired sign-in.',
        docsUrl: 'https://jamsql.com/docs/connections/#reconnecting',
      },
      {
        category: 'fix',
        id: 'schema-compare-diff-editor-crash',
        summary:
          'Schema Compare — fixed a crash in the side-by-side DDL diff that also affected every other diff view in the app.',
        docsUrl: 'https://jamsql.com/docs/schema-compare/',
      },
      {
        category: 'fix',
        id: 'clone-table-transaction-per-connection',
        summary:
          'Clone Table — each data-copy batch now runs in its own transaction on its own connection, so a rejected batch leaves no rows behind and two clones running at once never mix their rows.',
        docsUrl: 'https://jamsql.com/docs/clone/#if-the-data-copy-fails',
      },
      {
        category: 'fix',
        id: 'notebook-execution-fixes',
        summary:
          'SQL Notebooks — on SQL Server, cells now auto-commit like on the other engines (created tables are visible elsewhere, COMMIT works, #temp tables survive), and a cell with several statements shows every result set as its own tab.',
        docsUrl: 'https://jamsql.com/docs/sql-notebooks/#shared-session',
      },
      {
        category: 'improvement',
        id: 'trial-offer-after-any-sign-in',
        summary:
          'Pro trial — signing in from Settings, the start page or the trial dialog now offers the 14-day trial once when the account has no Pro subscription.',
      },
    ],
  },
  {
    version: '1.4.25',
    date: '2026-08-29',
    changes: [
      {
        category: 'feature',
        id: 'lookup-enums-reference-tables',
        summary:
          'Foreign-key columns can show the referenced row\'s name next to the key — `3 · Pending` instead of `3` — in grids, filters, and cell editing, while SQL, copy, and export keep the raw key.',
        details:
          'Three scopes, smallest to largest:\n\n' +
          '- **Lookup enum** — declare one FK column (the `▾?` hint or the header menu); the declare dialog opens as *"Show names from the referenced table"* with a key → name preview.\n' +
          '- **Reference table** — one checkbox marks the *target* table instead, and every column in the database that references it shows names, including tables you never opened. Explicit declarations still win.\n' +
          '- **On demand** — tables too big to cache (200+ rows) get names resolved per visible grid window from the FK popover\'s *"Show names from…"* button; nothing is stored.\n\n' +
          'Works on real and loose foreign keys across all five SQL engines. AI agents get the same metadata over MCP and can propose declarations with the new `metainfo_declare` tool — gated by the *"Allow AI to declare MetaInfo"* setting (default Deny) with a per-proposal approval dialog, and everything an agent saves is badged **AI-created** in the MetaInfo manager until you edit or re-save it yourself.',
        docsUrl: 'https://jamsql.com/docs/enum-columns/',
        blogUrl: 'https://jamsql.com/blog/2026-08-29-lookup-enums-reference-tables/',
      },
      {
        category: 'improvement',
        id: 'query-error-one-click-actions',
        summary:
          'Query Editor — error tips now offer a one-click next step: "Switch database…" opens the database selector when an object isn\'t found in the current database, and "Find the blocking session" opens the Session Browser when a query is waiting on a lock.',
        docsUrl: 'https://jamsql.com/docs/query-editor/#messages-and-output',
      },
      {
        category: 'improvement',
        id: 'execute-selection-relabel',
        summary:
          'Query Editor — the toolbar Execute button turns into "Selection" when a runnable statement is selected, whether the inline Execute / Peek bar is on or off.',
        docsUrl: 'https://jamsql.com/docs/query-editor/#execute-selection',
      },
      {
        category: 'fix',
        id: 'trial-signin-handoff',
        summary:
          'Pro trial — starting a trial no longer gets lost when sign-in finishes in the browser after the dialog was closed or the app restarted; the trial offer reappears once you’re signed in.',
      },
      {
        category: 'improvement',
        id: 'oracle-connection-error-fixes',
        summary:
          'Connections — Oracle connection errors now offer one-click fixes: an expired password (ORA-28001) can be reset right from the error notice, and an unknown service name (ORA-12514) offers to check which common service names the listener knows and retry with your pick.',
        docsUrl: 'https://jamsql.com/docs/connections/#oracle-connection-fixes',
      },
      {
        category: 'fix',
        id: 'table-explorer-open-flicker',
        summary: 'Table Explorer — the grid no longer flickers on load.',
        docsUrl: 'https://jamsql.com/docs/table-explorer/',
      },
      {
        category: 'fix',
        id: 'cursor-statement-row-limit-clauses',
        summary:
          'Query Editor — the statement under the cursor is now recognised when it ends in a row-limiting clause (Oracle’s FETCH FIRST … ROWS ONLY, OFFSET … ROWS) or IS NULL, so its highlight and run button appear without selecting the whole statement first.',
        docsUrl: 'https://jamsql.com/docs/query-editor/#execute-selection',
      },
      {
        category: 'improvement',
        id: 'ai-reads-user-run-schema-compare',
        summary:
          'AI Integrations — connected agents can now read the schema comparison you have open: each Schema Compare tab\'s current result is listed on the MCP compare snapshot, so an agent can fetch the differences or generate the sync script without re-running the comparison.',
        docsUrl: 'https://jamsql.com/docs/ai-integrations-mcp/',
      },
      {
        category: 'fix',
        id: 'schema-compare-module-ddl-after-table-alters',
        summary:
          'Schema Compare — generated sync scripts now create and update views, procedures and functions after the table changes, so a new procedure or view that reads a column the same script adds no longer fails with "Invalid column name".',
        docsUrl: 'https://jamsql.com/docs/schema-compare/#statement-order',
      },
      {
        category: 'feature',
        id: 'ai-export-results',
        summary:
          'AI Integrations — connected agents can now export a results grid to CSV, Excel, JSON, Markdown or SQL INSERT for you, including the streaming "Export All to CSV" for huge result sets; you pick the destination in the usual save dialog.',
        details:
          'The new `ui_export_results` MCP tool runs the same export the grid\'s own menu runs, so an agent-produced file is identical to one you export yourself — Excel typing, dialect-aware INSERT statements, spatial columns as WKT. The agent never chooses the path: the app opens its save dialog and you pick the destination or cancel. Works on Query Editor and Table Explorer tabs, and is refused for connections whose AI permission level is "Block".',
        docsUrl: 'https://jamsql.com/docs/ai-integrations-mcp/',
      },
      {
        category: 'fix',
        id: 'jam-sql-cli-query-output',
        summary:
          'jam-sql CLI — `query` now prints the table and CSV output it advertises; `--output table` and `--output csv` had been falling back to a raw JSON dump, and `--max-rows` / `--timeout` were being ignored.',
        docsUrl: 'https://jamsql.com/docs/ai-integrations-mcp/',
      },
      {
        category: 'fix',
        id: 'csv-export-server-side-mssql',
        summary:
          'Query Editor — huge "Export All to CSV" runs on SQL Server no longer hit request timeouts: the file is now written server-side by SQL Tools Service (falling back automatically to the previous method if that fails), and a row that still can’t be read after retries is skipped with a warning and listed in a "Skipped rows" tab beside the results — a preview and a one-click jump for each missing row — instead of failing the whole export.',
        docsUrl: 'https://jamsql.com/docs/query-editor/',
      },
    ],
  },
  {
    version: '1.4.24',
    date: '2026-08-27',
    changes: [
      {
        category: 'improvement',
        id: 'startup-session-skip',
        summary:
          'Sessions — a new "Always start a new session" option (on the startup dialog and in Settings → Sessions) skips the resume prompt at launch and lands on the Start Page; previous sessions stay available from the toolbar Sessions button.',
        docsUrl: 'https://jamsql.com/docs/sessions/',
      },
      {
        category: 'improvement',
        id: 'oracle-26ai-autocomplete',
        summary:
          'Query Editor — Oracle autocomplete now knows the 23ai/26ai SQL surface: QUALIFY, VECTOR_DISTANCE and the vector functions, DATEADD / DATEDIFF, and CREATE ASSERTION, each hint naming the Oracle version that accepts it.',
        blogUrl: 'https://jamsql.com/blog/2026-08-27-whats-new-oracle-26ai/',
      },
      {
        category: 'fix',
        id: 'csv-export-recovery-and-resume',
        summary:
          'Query Editor — a huge CSV export no longer fails when the server times out on very large cells; it retries with smaller batches, and an incomplete export can be resumed.',
        docsUrl: 'https://jamsql.com/docs/query-editor/',
      },
    ],
  },
  {
    version: '1.4.23',
    date: '2026-08-26',
    changes: [
      {
        category: 'feature',
        id: 'schema-compare-ignore-object-owner',
        summary:
          'Schema Compare — new "Ignore object owner" option (SQL Server): definitions differing only by the [dbo]. header qualifier no longer report as modified, offered right in the results when detected.',
        docsUrl: 'https://jamsql.com/docs/schema-compare/',
      },
      {
        category: 'feature',
        id: 'connection-import-discovery',
        summary:
          'Connections — Jam now spots saved connections from VS Code, SSMS, Azure Data Studio, DBeaver, MySQL Workbench, TablePlus, and ODBC, and offers one-click import from the start page and the New Connection dialog.',
        docsUrl: 'https://jamsql.com/docs/connections/',
      },
      {
        category: 'feature',
        id: 'connection-import-sqldeveloper',
        summary:
          "Connections — import your Oracle connections straight from SQL Developer's saved store or a connections.json export, service name / SID mapping included.",
        docsUrl: 'https://jamsql.com/docs/connections/',
      },
      {
        category: 'feature',
        id: 'mssql-2025-types',
        summary:
          "Table Designer — SQL Server 2025's native JSON and VECTOR(n) column types are now available when designing tables.",
        docsUrl: 'https://jamsql.com/docs/table-designer/',
        blogUrl: 'https://jamsql.com/blog/2026-08-25-whats-new-sql-server-2025/',
      },
      {
        category: 'improvement',
        id: 'connection-error-guidance',
        summary:
          'Connections — failed connections now explain what went wrong and offer one-click fixes (e.g. retry trusting the server certificate), and query errors gained inline tips for common causes.',
        docsUrl: 'https://jamsql.com/docs/connections/',
      },
      {
        category: 'fix',
        id: 'mssql-pool-stalls',
        summary:
          'SQL Server — queries no longer hang after laptop sleep or a dropped connection; stale pooled connections are detected and replaced within seconds.',
      },
    ],
  },
  {
    version: '1.4.22',
    date: '2026-08-24',
    changes: [
      {
        category: 'improvement',
        id: 'grid-scroll-performance',
        summary:
          'Results grids — scrolling, cell selection, and column resizing are dramatically faster on large and wide result sets, in both the Query Editor and Table Explorer.',
      },
      {
        category: 'fix',
        id: 'schema-compare-azure-create-or-alter',
        summary:
          'Schema Compare — Azure SQL targets are now correctly detected as supporting CREATE OR ALTER; no more false "predates 2016 SP1" notes or DROP+CREATE downgrades.',
        docsUrl: 'https://jamsql.com/docs/schema-compare/',
      },
      {
        category: 'feature',
        id: 'schema-compare-ignore-column-collation',
        summary:
          'Schema Compare — new "Ignore column collation" option, also offered right in the (now much shorter) collation banner; with it on, scripts never change a column\'s collation.',
        docsUrl: 'https://jamsql.com/docs/schema-compare/',
      },
      {
        category: 'improvement',
        id: 'data-compare-honest-sampled-results',
        summary:
          'Data Compare — a sampled comparison now says how many rows were not checked instead of claiming "no differences", with a one-click full comparison.',
        docsUrl: 'https://jamsql.com/docs/data-compare/',
      },
      {
        category: 'fix',
        id: 'command-palette-connection-actions',
        summary:
          'Command Palette — connecting from the palette shows connecting/connected/error feedback, and the pencil on a connection opens its edit dialog directly.',
      },
      {
        category: 'fix',
        id: 'table-explorer-pagination-jump',
        summary: 'Table Explorer — pagination controls no longer shift while a page loads.',
      },
    ],
  },
  {
    version: '1.4.21',
    date: '2026-08-22',
    changes: [
      {
        category: 'feature',
        id: 'sql-server-user-defined-types',
        summary:
          'SQL Server user-defined types are now first-class — they appear in the Object Explorer under Programmability → Types with Open definition and Script as CREATE / DROP, Schema Compare compares and scripts them in dependency order, and Database Blueprint emits and compares them.',
        docsUrl: 'https://jamsql.com/docs/schema-compare/',
      },
      {
        category: 'improvement',
        id: 'schema-compare-faster-and-accurate-lengths',
        summary:
          'Schema Compare — SQL Server comparisons read table metadata in a handful of set-based queries per side instead of six per table (5–15x faster on large databases, with per-side timing reported), and nvarchar/nchar lengths are now shown in characters rather than bytes.',
        docsUrl: 'https://jamsql.com/docs/schema-compare/',
      },
      {
        category: 'fix',
        id: 'copy-plans-and-explorer-fixes',
        summary:
          'Fixes — copy actions work again on Linux, estimated execution plans on MySQL 8.3+ / 9.x render the full operator tree, and connecting to a SQL Server instance with many databases no longer stalls the Object Explorer.',
      },
    ],
  },
  {
    version: '1.4.20',
    date: '2026-08-21',
    changes: [
      {
        category: 'fix',
        id: 'large-csv-export-timeout',
        summary:
          'Query Editor — a multi-million-row "Export All to CSV" on SQL Server no longer fails with "Request timeout for query/subset" when the server takes a slow moment; slow batches now wait far longer and retry, and if an export still fails the rows already written are kept on disk instead of being deleted.',
        docsUrl: 'https://jamsql.com/docs/query-editor/',
      },
      {
        category: 'improvement',
        id: 'export-progress-ux',
        summary:
          'Query Editor — the CSV export progress toast now shows an estimated time remaining, says "still working" instead of freezing during a slow batch, adds a Show in folder button when done, and long exports show their progress on the dock / taskbar icon.',
        docsUrl: 'https://jamsql.com/docs/query-editor/',
      },
      {
        category: 'fix',
        id: 'clipboard-copy-unfocused-window',
        summary:
          'Copying — copy actions no longer fail silently when the window loses focus mid-copy (e.g. alt-tabbing right after copying); all copies now go through the native clipboard.',
      },
    ],
  },
  {
    version: '1.4.19',
    date: '2026-08-20',
    changes: [
      {
        category: 'fix',
        id: 'schema-compare-wrapped-script-in-app-execution',
        summary:
          'Schema Compare — the transaction-wrapped sync script failed instantly with Msg 3997 when executed inside Jam SQL Studio on SQL Server; the wrap now works in-app exactly as it already did in SSMS and sqlcmd.',
        docsUrl: 'https://jamsql.com/docs/schema-compare/#single-transaction',
      },
      {
        category: 'improvement',
        id: 'blueprint-git-changed-file-list',
        summary:
          'Database Blueprint — the Git panel now lists every changed file (modified / new / deleted / conflicted); click one to preview it before committing.',
        docsUrl: 'https://jamsql.com/docs/database-blueprint/#git-integration',
      },
    ],
  },
  {
    version: '1.4.18',
    date: '2026-08-13',
    changes: [
      {
        category: 'feature',
        id: 'schema-and-data-compare-improvements',
        summary:
          'Schema and data compare improvements — depth presets with saved defaults, shareable settings files, single-transaction sync scripts, member-level selection, Recently compared with pinning, Apply as INSERT, ~20x faster SQL Server comparisons, and a long run of script-correctness and disclosure fixes.',
        docsUrl: 'https://jamsql.com/docs/schema-compare/',
        blogUrl: 'https://jamsql.com/blog/schema-and-data-compare-improvements/',
      },
      {
        category: 'feature',
        id: 'blueprint-apply-to-linked-dbs',
        summary:
          'Database Blueprint — "Apply schema to linked DBs…" compares the folder against every linked database in one pass and reports what each one\'s script would add, change, or remove; nothing is executed for you.',
        docsUrl: 'https://jamsql.com/docs/database-blueprint/#consolidate-many-databases',
      },
      {
        category: 'feature',
        id: 'dependency-viewer-find-usages',
        summary:
          '"Find usages" searches SQL definition text for a column or term across all five engines (a lead, not proof — it also matches comments and dynamic SQL), cross-checked on SQL Server and PostgreSQL against what the database catalog itself recorded; large or table-object dependency graphs also default to a readable tree view, and filtering the graph now hides non-matching nodes instead of just dimming them.',
        docsUrl: 'https://jamsql.com/docs/dependency-viewer/#find-usages',
      },
      {
        category: 'feature',
        id: 'settings-and-connections-transfer',
        summary:
          'Settings → Backup & Transfer — export and import your app settings and, separately, your saved connections to move your setup to another machine or share it with a teammate; exported connection secrets are passphrase-encrypted and never written in plain text.',
        docsUrl: 'https://jamsql.com/docs/getting-started/#backup-transfer',
      },
      {
        category: 'feature',
        id: 'toolbar-customization',
        summary:
          'Toolbar — customize which buttons live on the primary toolbar versus the More ▾ menu, from Settings → UI → Toolbar.',
        docsUrl: 'https://jamsql.com/docs/getting-started/#customize-toolbar',
      },
      {
        category: 'improvement',
        id: 'dependency-viewer-open-definition',
        summary:
          'Dependency Viewer — open a definition straight from the dependency list via a hover button or the right-click menu, and PostgreSQL lists now include the views and materialized views that read a table, which were silently missing.',
        docsUrl: 'https://jamsql.com/docs/dependency-viewer/',
      },
      {
        category: 'fix',
        id: 'blueprint-apply-safety',
        summary:
          'Database Blueprint — Apply schema no longer drops an object you excluded (single link, batch, or an AI agent over MCP), a linked database records its baseline instead of reporting drift its own refresh created, and a batch apply compares only what the blueprint covers.',
        docsUrl: 'https://jamsql.com/docs/database-blueprint/#excluding-objects',
      },
      {
        category: 'fix',
        id: 'connection-and-tunnel-fixes',
        summary:
          'Connections — cancelling a connect or a connection test really stops it, and SSH host-key pins moved to ~/.ssh/jam_known_hosts (existing pins move on upgrade) so ssh-keygen works on your own known_hosts again.',
        docsUrl: 'https://jamsql.com/docs/connections/#ssh-tunneling',
      },
      {
        category: 'fix',
        id: 'engine-specific-fixes',
        summary:
          'Engine fixes — Clone Table with data works on MySQL and Oracle, a PostgreSQL connection is usable straight after a script fails inside a transaction, and Oracle PL/SQL breakpoints can be set again.',
        docsUrl: 'https://jamsql.com/docs/clone/',
      },
      {
        category: 'fix',
        id: 'query-editor-and-app-ui-fixes',
        summary:
          'Query Editor and app UI — the execute confirmation reads only what the engine will run, so a comment no longer triggers a destructive warning; a menu opened right after results stays open; More-menu items stay enabled with several connections; execution plans survive a narrow window; and quitting no longer loops error dialogs.',
        docsUrl: 'https://jamsql.com/docs/query-editor/#destructive-confirmation',
      },
    ],
  },
  {
    version: '1.4.17',
    date: '2026-07-24',
    changes: [
      {
        category: 'improvement',
        id: 'spatial-explorer-polish',
        summary:
          'Spatial Explorer — layer label columns now render on the map, a zoom-to-fit control restores the all-layers view, and the Add Layer dialog and layer cards got usability fixes.',
        docsUrl: 'https://jamsql.com/docs/spatial/',
      },
      {
        category: 'improvement',
        id: 'export-show-in-folder',
        summary:
          'Export — the completion notice can reveal the exported file in your file manager, and Excel now leads the export menus.',
      },
      {
        category: 'fix',
        id: 'schema-compare-as-null-computed-column',
        summary:
          'Schema Compare — adding a missing column no longer generates computed-column syntax (AS (NULL)) instead of the real data type on SQL Server connections.',
      },
      {
        category: 'improvement',
        id: 'connection-database-scoping',
        summary:
          "Connections — a new 'Limit connection to selected database' option scopes Object Explorer, database pickers, and AI schema export to a single database, and compare/migration dialogs now default to the connection's saved database.",
        docsUrl: 'https://jamsql.com/docs/connections/#limit-connection-to-selected-database',
      },
      {
        category: 'improvement',
        id: 'local-sql-server-detection',
        summary:
          "Connections — Detect local db's now finds native SQL Server instances (SQLEXPRESS, LocalDB) on Windows alongside Docker containers.",
        docsUrl: 'https://jamsql.com/docs/connections/#detect-local-docker-databases',
      },
      {
        category: 'improvement',
        id: 'ai-workspace-setup-consent',
        summary:
          'AI Workspace — asks for confirmation (with target database and an explanation of what gets exported) before setting up, and the setup overlay gains a Cancel button.',
      },
    ],
  },
  {
    version: '1.4.16',
    date: '2026-07-19',
    changes: [
      {
        category: 'fix',
        id: 'ssts-empty-database-listing',
        summary:
          'SQL Server — local instances reached through SQL Tools Service no longer report "No databases found" when loading databases or browsing the Object Explorer.',
      },
      {
        category: 'feature',
        id: 'data-import-wizard',
        summary:
          'Data Import — a new wizard imports CSV, TSV, Excel, and JSON/NDJSON files into a new or existing table across all five engines, with column mapping and type control, fixed-value columns, row filters, a generate-script option, and optional post-import metadata suggestions.',
        docsUrl: 'https://jamsql.com/docs/import-data/',
      },
      {
        category: 'improvement',
        id: 'connection-add-edit-improvements',
        summary:
          'Connections — smoother adding and editing: reliable Test Connection and Load databases, a clear notice when databases can\'t be listed, and no ~30-second stall on local SQL Server instances.',
      },
    ],
  },
  {
    version: '1.4.15',
    date: '2026-07-09',
    changes: [
      {
        category: 'feature',
        id: 'kusto-adx-engine',
        summary:
          'Azure Data Explorer (KQL) — connect to ADX clusters and App Insights/Log Analytics, run KQL with IntelliSense in the editor and SQL Notebooks, browse tables read-only with enum, JSON-path and loose-foreign-key filter chips, and chart | render results.',
        docsUrl: 'https://jamsql.com/docs/kusto-kql/',
      },
      {
        category: 'feature',
        id: 'azure-browse',
        summary:
          'Connections — Browse Azure picks ADX clusters, App Insights, Log Analytics, Azure SQL and PostgreSQL flexible servers straight from your Azure account (Add Connection + Import).',
        docsUrl: 'https://jamsql.com/docs/connections/#browse-azure',
      },
      {
        category: 'feature',
        id: 'oracle-property-graphs',
        summary:
          'Object Explorer — Oracle 23ai SQL/PGQ property graphs appear under a Property Graphs node with Script as CREATE / DROP and a GRAPH_TABLE starter-query template.',
        docsUrl: 'https://jamsql.com/docs/scripting/',
      },
      {
        category: 'feature',
        id: 'pgsql19-support',
        summary:
          'PostgreSQL 19 (beta) support — SQL/PGQ property graphs, REPACK maintenance scripts, partition MERGE/SPLIT, autovacuum scores, EXPLAIN IO statistics, case-insensitive JSON filters, and more.',
        docsUrl: 'https://jamsql.com/blog/whats-new-postgresql-19/',
      },
      {
        category: 'fix',
        id: 'notebook-oracle-execute',
        summary:
          'SQL Notebooks — fixed Oracle connections failing to run any cell ("execute is not a function"); notebooks now fully support Oracle with pinned sessions.',
      },
      {
        category: 'feature',
        id: 'pinned-workspaces',
        summary:
          'Object Explorer — pin Table Explorer views, queries, and notebooks to the connection tree and reopen them with a double-click.',
        docsUrl: 'https://jamsql.com/docs/table-explorer/#pin-to-object-explorer',
      },
      {
        category: 'feature',
        id: 'spatial-postgis',
        summary:
          'Spatial / PostGIS — map geometry & geography columns across PostgreSQL, MySQL, SQL Server, Oracle and SQLite with peek maps, a Query Editor Map view, a Table Explorer map pane with bounding-box, distance and draw-an-area spatial filters, a Spatial Explorer workspace, and curved-geometry rendering (basemap on by default).',
        docsUrl: 'https://jamsql.com/docs/spatial/',
      },
      {
        category: 'fix',
        id: 'schema-compare-sync-script-semicolon',
        summary:
          'Schema Compare — generated sync scripts no longer append a semicolon to SQL Server procedure, function, and view definitions, so a re-compare after applying no longer reports the same objects as modified.',
      },
    ],
  },
  {
    version: '1.4.14',
    date: '2026-07-04',
    changes: [
      {
        category: 'feature',
        id: 'connection-string-paste',
        summary:
          'Connections — paste a connection string (ADO.NET, URL, JDBC, EZConnect, libpq, SQLite path) into the New Connection dialog or the command palette to prefill the form.',
        docsUrl: 'https://jamsql.com/docs/connections/',
      },
      {
        category: 'feature',
        id: 'connection-import',
        summary:
          'Connections — import your saved connections in one click from other database tools (Azure Data Studio, VS Code, SSMS, DBeaver, DataGrip, TablePlus, MySQL Workbench, Navicat, pgAdmin) and standard config files (tnsnames.ora, pg_service.conf, .my.cnf, ODBC), via the new Import… menu, the command palette, or the start page.',
        details: [
          'Pick a source from the **Import…** dropdown in Manage Connections, the command palette, or a start-page card — sources with a saved store on this machine are highlighted.',
          '',
          "Jam SQL Studio **never** reads another app's OS keychain or decrypts its stored passwords: passwords are only prefilled from documented plaintext files (`.pgpass`, `.my.cnf`, `pg_service.conf`), and otherwise re-entered inline or on first connect. Import only saves connections — it never auto-connects.",
        ].join('\n'),
        docsUrl: 'https://jamsql.com/docs/connections/#import-connections',
      },
      {
        category: 'feature',
        id: 'docker-discovery',
        summary:
          'Connections — detect local Postgres, MySQL/MariaDB, SQL Server, and Oracle databases running in Docker and add them in one click, with credentials read straight from the container environment.',
        docsUrl: 'https://jamsql.com/docs/connections/#detect-local-docker-databases',
      },
      {
        category: 'fix',
        id: 'notebook-markdown-heading-styles',
        summary:
          'Notebook — Markdown headings in the cell preview now render at their proper heading size instead of appearing as plain body text.',
      },
      {
        category: 'fix',
        id: 'schema-compare-script-fixes',
        summary: 'Schema Compare — script generation and formatting fixes.',
        details: [
          '- **Select all** now selects only the objects visible under the active filter.',
          '- Generated scripts include **computed column** definitions.',
          '- Re-comparing after applying a generated script no longer flags procedures and views as modified (trailing semicolon).',
          '- SQL formatting keeps the required parentheses around **CREATE FUNCTION** parameters.',
        ].join('\n'),
      },
      {
        category: 'improvement',
        id: 'query-tab-title-ddl',
        summary:
          'Query tabs — a script that scripts an object (e.g. CREATE OR ALTER PROCEDURE) is now titled after the object, like GetUsers(Create/Alter), even when a leading USE/GO preamble precedes it.',
      },
      {
        category: 'fix',
        id: 'crash-dialog-network-noise',
        summary:
          'Crashes — brief network interruptions (Wi-Fi switch, sleep/wake, VPN) no longer show a crash dialog, and a repeating crash can no longer spam dialogs.',
      },
      {
        category: 'feature',
        id: 'json-peek-filter-aware',
        summary:
          "Table Explorer — JSON path suggestions in the filter chip's peek popover now match the grid's active filters (with a whole-column escape), so polymorphic JSON columns suggest the right variant's properties.",
        docsUrl: 'https://jamsql.com/docs/json-columns/',
      },
    ],
  },
  {
    version: '1.4.13',
    date: '2026-06-16',
    changes: [
      {
        category: 'improvement',
        id: 'blueprint-sync-and-reliability',
        summary:
          'Database Blueprint — Sync Data now round-trips Unicode text, very large integers, and MySQL date/backslash values losslessly, and Refresh from DB keeps the data sidecar files of still-selected tables. Failure reporting is clearer throughout: the git panel blocks committing with unresolved merge conflicts, drift-scan and git-status errors surface properly, and disabled buttons explain why.',
        docsUrl: 'https://jamsql.com/docs/database-blueprint/',
      },
      {
        category: 'fix',
        id: 'assorted-crash-fixes',
        summary:
          'Fixed crashes when viewing a Blueprint file diff after reconnecting, switching between Schema Compare object diffs, re-running a query tab twice in quick succession, exporting results that contain an invalid date, rapidly switching between query tabs (a rare editor render crash), and a phantom "Query timeout" crash dialog that could appear seconds after a SQL Server connection failure.',
      },
    ],
  },
  {
    version: '1.4.12',
    date: '2026-06-13',
    changes: [
      {
        category: 'improvement',
        id: 'timeout-handling',
        summary: 'Improved timeout handling for SQL Server queries and connections.',
      },
      {
        category: 'feature',
        id: 'cross-engine-schema-compare',
        summary: 'Schema Compare — limited support for comparing two databases on different engines.',
        details:
          'Point Schema Compare at a source and target on **different engines** (for example SQL Server vs PostgreSQL) and it compares **tables in full** on a canonical type model — columns, types, nullability, keys, constraints, and indexes — plus a name-presence inventory of views, procedures, functions, and triggers so you can see what is missing on each side.\n\nObject **bodies** (T-SQL vs PL/pgSQL) are never auto-translated, and sequences are not compared across engines — those stay manual.',
        docsUrl: 'https://jamsql.com/docs/schema-compare/#cross-engine',
        blogUrl: 'https://jamsql.com/blog/2026-06-13-migrate-sql-server-to-postgresql/',
      },
      {
        category: 'feature',
        id: 'cross-engine-migrate',
        summary:
          "Cross-engine migration — copy a table's full schema and data to a connection of any engine (e.g. SQL Server → PostgreSQL).",
        details:
          "New **Migrate Table** and **Migrate all tables to Connection…** actions copy a table's full schema — types, identity, defaults, CHECK/UNIQUE, foreign keys (with ON DELETE/UPDATE), filtered + covering indexes, computed columns, and user-defined alias types — plus its **data** to a connection of any engine.\n\nThe migration runs in its own workspace tab with live per-table progress, streams data in chunks so large tables never load into memory, and can be cancelled mid-run with a prompt to drop or keep partially-copied tables. A **Preview** scripts the migration and shows the exact row count it will copy (a 50-row sample, with a banner noting the rest is streamed chunk by chunk), and a “needs manual follow-up” checklist flags anything engine-specific.",
        docsUrl: 'https://jamsql.com/docs/cross-engine-database-migration/',
        blogUrl: 'https://jamsql.com/blog/2026-06-13-migrate-sql-server-to-postgresql/',
      },
      {
        category: 'fix',
        id: 'schema-compare-datetime2-precision',
        summary:
          'Schema Compare (SQL Server) — the generated synchronization script now includes ALTER COLUMN statements for datetime2/datetimeoffset/time precision changes that the overview flagged as "length changed" but the script previously dropped.',
      },
    ],
  },
  {
    version: '1.4.11',
    date: '2026-06-03',
    changes: [
      {
        category: 'fix',
        id: 'ssh-fix',
        summary: 'SSH issue resolved.',
      },
    ],
  },
  {
    version: '1.4.10',
    date: '2026-05-25',
    changes: [
      {
        category: 'feature',
        id: 'ssh-tunneling',
        summary:
          'Connect to PostgreSQL, MySQL, Oracle, and SQL Server through an SSH bastion — no manual port-forwards.',
        details: [
          'Reach databases behind a firewall by checking **Connect via SSH tunnel** in the connection dialog and filling in the SSH host, port, user, and auth method.',
          '',
          '- **Auth methods:** SSH agent, private-key (with an optional keychain-saved passphrase), or password (also keychain-saveable).',
          '- **`~/.ssh/config` autofill:** start typing a Host alias to auto-fill SSH host, user, port, and key file.',
          '- **Stepwise Test Connection:** reports the SSH leg and the database leg separately, so you can tell exactly which side needs attention.',
        ].join('\n'),
        docsUrl: 'https://jamsql.com/docs/connections/#ssh-tunneling',
      },
      {
        category: 'feature',
        id: 'object-explorer-pinning',
        summary:
          'Pin databases, schema containers, folders, and individual objects to a list above Databases under each connection.',
        details: [
          'Hover any pinnable row and click the outline pin on the right; it becomes a filled pin that stays visible.',
          '',
          '- Pinned rows from the same database are grouped into subtle tinted areas with a tiny db-name header (a pinned database with no same-DB siblings renders standalone; single-accessible-DB connections hide the header).',
          '- Pinned rows are expandable shortcuts that drill into the same children as the canonical tree.',
          '- Drag a pinned row to reorder; a cross-DB drop simply splits the grouping in place — the pin keeps its database.',
          '- Removed-or-missing targets show greyed with a "no longer present" tooltip.',
          '- When pins exist, Databases no longer auto-expands on connect, and the sidebar search excludes the shortcuts so a pinned row never shows up twice.',
        ].join('\n'),
        docsUrl: 'https://jamsql.com/docs/getting-started/#pinning-objects',
      },
      {
        category: 'feature',
        id: 'database-blueprint',
        summary:
          'Database Blueprint (beta) — materialize a database\'s full schema and reference data as a folder of .sql files, kept in two-way sync with your live databases.',
        docsUrl: 'https://jamsql.com/docs/database-blueprint/',
        blogUrl: 'https://jamsql.com/blog/2026-05-23-database-blueprint/',
      },
      {
        category: 'improvement',
        id: 'data-compare',
        summary:
          'Data Compare — clearer empty and error states, plus several reliability fixes.',
        details: [
          '- A comparison with no differences shows an explicit "Source and target data has no differences" message instead of an empty grid.',
          '- A failed comparison (missing column, inaccessible table, dropped connection) now shows the error in the results area instead of silently returning to the empty state.',
          '- Switching the compared table re-detects the value/key column mappings, so you no longer get blank Value Columns or an "Invalid column name" failure.',
          '- Collation-independent merge-join ordering eliminates phantom inserts/deletes on binary collations (e.g. PostgreSQL `COLLATE "C"`).',
          '- Oracle row-count estimation reads both upper- and lowercase `cnt` aliases, so the Personal-mode row-count cap is no longer silently bypassed.',
        ].join('\n'),
        docsUrl: 'https://jamsql.com/docs/data-compare/',
      },
      {
        category: 'feature',
        id: 'te-breadcrumb',
        summary:
          'Table Explorer — a foreign-key navigation breadcrumb: drill through real and loose FKs in the same tab, with join-table collapse and keyboard navigation.',
        details: [
          '- Foreign-key clicks drill into the referenced row in the same tab via a clickable breadcrumb tree; `Cmd`-click (`Ctrl`-click on Windows / Linux) or the **⋯** menu still opens a new tab.',
          '- Loose foreign keys are first-class hops, navigable identically to declared FKs.',
          '- Pure m2m join tables auto-collapse from the breadcrumb; right-click a chip → "Show join tables" to override per-table.',
          '- `Cmd+←` `Cmd+→` `Cmd+↑` `Cmd+↓` (`Ctrl` on Windows / Linux) traverse the breadcrumb tree.',
        ].join('\n'),
        docsUrl: 'https://jamsql.com/docs/table-explorer/#foreign-key-navigation',
        blogUrl: 'https://jamsql.com/blog/2026-05-24-foreign-key-navigation-breadcrumb/',
      },
      {
        category: 'feature',
        id: 'te-single-row-details',
        summary:
          'Table Explorer — a new Single-Row Details View shows a row\'s cells (categorized) and its related inbound-FK data side-by-side.',
        details: [
          'A full-workspace view shows row cells categorized into Identity / Profile / Foreign keys / State / Timestamps / Counters / Metadata, alongside related (inbound-FK) data.',
          '',
          'Related Data sections load lazily so the view opens fast even on wide rows.',
        ].join('\n'),
        docsUrl: 'https://jamsql.com/docs/table-explorer/',
      },
      {
        category: 'feature',
        id: 'cell-edit-pickers',
        summary:
          'Cell edit — enum columns show a values peek and foreign-key columns offer a row picker, in both the Query Editor results grid and Table Explorer.',
        details: [
          'Editing a cell in a declared or native enum column shows a values peek with live filtering (free text always works as a fallback).',
          '',
          'Editing a cell in a real or loose foreign-key column offers a **Search row in …** link that opens the same row picker used by filters; polymorphic loose FKs list one entry per matching declaration.',
          '',
          'Both pickers work in the Query Editor results grid and in Table Explorer.',
        ].join('\n'),
        docsUrl: 'https://jamsql.com/docs/table-explorer/',
      },
      {
        category: 'feature',
        id: 'mcp-connections-add',
        summary:
          'MCP & CLI — AI agents can add new database connections via the new connections_add MCP tool and jam-sql connection add CLI command.',
        details: [
          'AI agents can add a new connection through the `connections_add` MCP tool or the `jam-sql connection add` CLI command.',
          '',
          '- **Off by default** — enable in Settings → AI Integrations → *Allow AI to add new connections* (Deny / Confirm each / Allow).',
          '- The tool test-connects before saving and encrypts the password at rest.',
        ].join('\n'),
        docsUrl: 'https://jamsql.com/docs/ai-integrations-mcp/',
        blogUrl: 'https://jamsql.com/blog/connect-ai-agents-database-mcp/',
      },
      {
        category: 'fix',
        id: 'schema-compare-loud-fetch-error',
        summary:
          'Schema Compare — a per-object fetch error mid-comparison now surfaces a clear error naming the failing object, instead of silently dropping it.',
        details: [
          'If a per-object metadata or definition fetch errors mid-comparison (e.g. a stale connection after macOS sleep), Schema Compare now names the failing object instead of silently dropping it from the snapshot.',
          '',
          'Previously the missing object would be flagged "only in source" on every run, and the generated `CREATE` script would fail with MSSQL error 2714 ("already exists").',
        ].join('\n'),
        docsUrl: 'https://jamsql.com/docs/schema-compare/',
      },
      {
        category: 'fix',
        id: 'destructive-query-confirmation',
        summary:
          'Destructive-query confirmation — routine bodies no longer false-trigger the dialog, and top-level DROP of routines/views shows a milder, distinctly-worded warning.',
        details: [
          '`CREATE` / `ALTER PROCEDURE`, `FUNCTION`, `TRIGGER`, and `PACKAGE` / `TYPE` bodies no longer false-trigger the dialog when their bodies happen to contain `DELETE`, `TRUNCATE`, or `DROP` keywords (the routine wrapper is inert at submission).',
          '',
          'A top-level `DROP PROCEDURE` / `FUNCTION` / `TRIGGER` / `VIEW` now shows a milder, distinctly-worded confirmation — the strong "permanent data destruction" warning is reserved for actual data destruction (`DROP TABLE`, `TRUNCATE`, `DELETE` / `UPDATE` without `WHERE`).',
          '',
          'Applies to MSSQL, PostgreSQL, MySQL, Oracle, and SQLite.',
        ].join('\n'),
        blogUrl: 'https://jamsql.com/blog/prevent-accidental-production-queries/',
      },
      {
        category: 'improvement',
        id: 'query-editor-timeout',
        summary:
          'Query Editor — the default query timeout is raised from 15 s to 5 min, with a clearer long-running indicator and a one-click Stop.',
        details: [
          'Long-running queries no longer feel stuck:',
          '',
          '- The default query timeout is raised from **15 s to 5 min** (set Settings > Advanced > Query timeout to `0` for no client-side limit). Existing installs on the legacy 15 s default are auto-migrated to 5 min on next launch.',
          '- After 8 s the status-bar execution timer turns amber and pulses, and the "Executing query…" indicator changes to "Executing query… taking longer" with a one-click **Stop** — nothing shifts in the toolbar.',
          '- Multi-batch `CREATE OR ALTER` / schema-compare apply scripts on SQL Server now execute batch-by-batch through the direct driver, so they aren\'t throttled by SSTS notification overhead.',
        ].join('\n'),
        docsUrl: 'https://jamsql.com/docs/query-editor/',
      },
      {
        category: 'fix',
        id: 'licensing-account',
        summary:
          'Licensing — sign-ins now stay signed in, and "Verify Now" is honest with a clear re-logon prompt.',
        details: [
          'Fixed a regression where signing in no longer kept you signed in: the auth request stopped requesting offline_access, so no refresh token was issued and your Pro session asked for re-authentication about an hour after every sign-in.',
          '',
          '"Verify Now" on the offline-license banner no longer silently does nothing when your session has lapsed — it reports the outcome and, when the session can\'t be refreshed, prompts you to sign in again.',
          '',
          'A stale Pro session now surfaces a clear re-logon banner (sessions otherwise stay sticky — no routine logouts), and proactive token renewal runs in the main process so sessions survive sleep/wake.',
        ].join('\n'),
      },
      {
        category: 'feature',
        id: 'object-explorer-schema-node',
        summary:
          'Object Explorer — schema nodes get Script New and Script Schema As context-menu actions.',
        details: [
          'Right-click a schema node for new context-menu actions:',
          '',
          '- **Script New** — Table / View / Stored Procedure / Function templates scoped to the schema.',
          '- **Script Schema As** — `CREATE` / `DROP SCHEMA` on SQL Server and PostgreSQL, plus **Script all objects** to script every table, view, procedure, and function in the schema.',
        ].join('\n'),
        docsUrl: 'https://jamsql.com/docs/scripting/',
      },
    ],
  },
  {
    version: '1.4.9',
    date: '2026-05-21',
    highlights: 'JSON & enum column support and filtered loose foreign keys — declare columns as JSON or enum, peek JSON shape, loose FKs on JSON paths, plus source/target filter scoping for polymorphic and soft-delete-aware references',
    changes: [
      {
        category: 'feature',
        description:
          'Filtered loose foreign keys — Loose FKs now support optional source and target filters. A source filter scopes when the FK applies (e.g. polymorphic associations: `commentable_id → posts.id` only when `commentable_type = \'Post\'`). A target filter narrows valid target rows (e.g. soft-delete-aware: `deleted_at IS NULL`). A new peek pane in the editor shows live counts and a 5-row sample so you can validate filters by eye.',
      },
      {
        category: 'feature',
        description:
          'JSON column support. From the Table Explorer filter chip, a new `json` operator works on native JSON columns and on long-text columns you declare as JSON, with property predicates (`=`, `!=`, `contains`, `>`, `≥`, `<`, `≤`, existence, validity), JSONPath wildcards (`$.items[*].qty`), and `any`/`all` aggregate sub-operators. An eye-icon button next to the JSONPath input peeks the JSON shape — a popover listing every property path observed for that column. Loose foreign keys can also be declared on JSON property paths so a value inside a JSON document behaves like a real FK. Right-click a declared column to open a *JSON details* dialog with the inferred property paths, a *Refresh from DB* re-sample, an optional inference filter, and a *Remove JSON declaration* action — the same Refresh + Filter affordance also appears in the MetaInfo manager, Settings → Meta Info, and the *Declare as JSON* confirmation.',
      },
      {
        category: 'feature',
        description:
          'Enum column support. Declare a column as an enum and its filter chip becomes a values dropdown — sourced from native enum types, `CHECK` constraints, or sampled distinct values, on all 5 engines. Header glyphs and an *Enum values* details dialog manage declarations, which round-trip through MetaInfo export/import.',
      },
      {
        category: 'feature',
        description:
          'Filter Wizard — *Peek matching rows*. A new eye-icon button in every FilterWizard header (Table Explorer, JSON details, Declare-as-JSON) runs the current filters as a `SELECT * LIMIT 50` against the underlying table and shows the matching rows in an inline popover. Useful for sanity-checking a filter before sampling JSON structure or exporting filtered data.',
      },
      {
        category: 'feature',
        description:
          'Table Explorer filters — multiple filters on the same column. You can now stack two or more filters on a single column (e.g. `created_at >= …` AND `created_at < …`, or two different `json property =` predicates on the same JSON column), which previously was blocked by a "one filter per column" rule.',
      },
      {
        category: 'improvement',
        description:
          'Schema Compare — formatting and clearer diff UI. Routine/view/trigger DDL is now formatted before comparison so indentation- or keyword-case-only drift no longer shows as Modified (toggle *Format SQL during comparison*, or flip per-diff with the *Formatted / Raw* pill). The diff reads Source-on-left with connection/database labels, a direction toggle generates the sync script either way (Source → Target / Target → Source) without re-running, and the Script Preview dialog has a split-button *Open & Execute* action.',
      },
      {
        category: 'improvement',
        description:
          'Results grid — more consistent copy. Ctrl+C on a multi-cell selection now copies both plain text and a formatted HTML table, so pasting into Teams, Outlook, or Word renders a real table. A new *Copy as HTML* option is also available in the copy-to-clipboard menu (both Query Editor and Table Explorer).',
      },
      {
        category: 'improvement',
        description:
          'JSON column ergonomics. Header glyphs now show three distinct, clickable states — a blue `{}` for columns with a JSON declaration, a gray `{}` for undeclared native JSON columns, and a gray `{}?` for long-text columns whose rows look like JSON; clicking the blue glyph opens the JSON details inspector, either gray glyph opens the declare dialog, and a *Declare as JSON* CTA appears atop the Expanded Value dialog when a cell parses as JSON. Native `json` / `jsonb` columns can now carry a declaration too — it attaches path/loose-FK metadata without changing SQL emission. On SQL Server, filters that rely on `OPENJSON` (wildcard paths, `any_*` / `all_*` / `is empty`) now detect database `COMPATIBILITY_LEVEL < 130`, warn inline with a *Recheck* link, keep scalar predicates working via `JSON_VALUE` / `ISJSON`, and suggest `ALTER DATABASE [<db>] SET COMPATIBILITY_LEVEL = 130;` when an `OPENJSON` error surfaces.',
      },
      {
        category: 'improvement',
        description:
          'Table Explorer — drag-and-drop column reordering. Drag a column header to rearrange columns in the grid; the new order is saved per tab and restored on the next app launch.',
      },
    ],
  },
  {
    version: '1.4.8',
    date: '2026-05-10',
    changes: [
      {
        category: 'improvement',
        description:
          'Schema Compare — improved diff UI. Table diffs now surface dependent-object changes (indexes, foreign keys, triggers) and detect column, table-option, and partition differences.',
      },
      {
        category: 'fix',
        description:
          'Schema Compare — the schema filter in the setup dialog now correctly narrows comparisons by schema.',
      },
      {
        category: 'improvement',
        description:
          'Connection and database listing — clearer error messages when something goes wrong, with an option to send a redacted diagnostic report.',
      },
    ],
  },
  {
    version: '1.4.7',
    date: '2026-05-06',
    changes: [
      {
        category: 'improvement',
        description:
          'Better diagnostics when listing databases fails — click *Show details* to see the real cause, and optionally send a redacted diagnostic report.',
      },
    ],
  },
  {
    version: '1.4.6',
    date: '2026-05-04',
    highlights: 'Schema Compare overview, recently compared shortcuts, and stability fixes',
    changes: [
      {
        category: 'feature',
        description:
          'Schema Compare overview — a new high-level summary of a comparison run. See total counts of added, removed, and changed objects grouped by category, with object-level cards you can drill into without leaving the overview.',
      },
      {
        category: 'improvement',
        description:
          'Schema Compare — Recently compared. The setup dialog now remembers source/target databases from previous comparisons and surfaces them at the bottom of the dialog. Pick the most recent run with one click; expand to see older comparisons, with a search box (visible when there are two or more) that filters by connection or database name. Each entry can be removed individually.',
      },
      {
        category: 'fix',
        description:
          'Schema Compare — stability fixes for the comparison run, including better error reporting when a comparison fails to start.',
      },
    ],
  },
  {
    version: '1.4.5',
    date: '2026-05-01',
    highlights: 'Loose foreign keys — declare cross-table relationships even when the database has no FK constraint',
    changes: [
      {
        category: 'feature',
        description:
          'Microsoft Entra ID (Interactive Browser) — new MSSQL auth mode that opens your default browser for a standard OAuth sign-in.\n\nUse it when your tenant blocks the Device Code flow, or to skip entering a user code. Cached sign-in and Tenant ID override apply to both Entra modes.',
      },
      {
        category: 'feature',
        description:
          'Loose foreign keys — declare cross-table relationships even when the database has no FK constraint.\n\nPick "lookup" on any column in Table Explorer or Data Profiling filters; the picker pre-fills an inferred target. Once confirmed, the column behaves like a real FK.',
      },
      {
        category: 'feature',
        description:
          'Loose-FK cell links — result grids render loose-FK values as clickable links: teal text with a dashed underline and a chain icon, visually distinct from real FKs.\n\nClick opens the row-preview popover with a "Clear loose relation" action. Works in Query Editor results and Table Explorer.',
      },
      {
        category: 'feature',
        description:
          'Schema Overview & Dependency Viewer — declared loose relationships render as dashed blue edges with a "Loose" badge on connected nodes, so user-declared references stay visually distinct from enforced ones.',
      },
      {
        category: 'feature',
        description:
          'Row Details — the Related-data tab now lists inbound loose-FK rows alongside real-FK inbound, each marked with a "Loose" badge so you can navigate declared parents and children from the same panel.',
      },
      {
        category: 'feature',
        description:
          'MetaInfo manager dialog — open from the Link2 icon in the Table Explorer toolbar or by right-clicking a database in the Object Explorer ("Manage meta info..."). List/remove declarations, or Export/Import the whole MetaInfo file as JSON to share across machines.\n\nMetaInfo is the foundation for future per-database metadata (table descriptions, AI context, tags).',
      },
    ],
  },
  {
    version: '1.4.4',
    date: '2026-04-30',
    highlights: 'Data profiling, foreign-key lookup picker, and a tidier query editor',
    changes: [
      { category: 'feature', description: 'Data Profiling workspace — inspect any column or table without writing SQL. Right-click a column or table in the Object Explorer and choose Profile data to open a dedicated tab with summary stats, histograms, and top values.' },
      { category: 'feature', description: 'Foreign-key lookup picker in Table Explorer — filter FK columns without typing IDs. Pick a row from the parent table via a searchable dialog with per-column filters and unbounded FK drill-down.' },
      { category: 'improvement', description: 'Table Explorer — streaming row loads, page sizes up to 10,000, parallel data + COUNT queries, "Add to filter" on cell right-click, and a redesigned pagination strip with inline progress.' },
      { category: 'improvement', description: 'Inline toolbar is now optional — the floating Execute / Peek bar above the cursor statement is off by default. Selection-runnable SQL is surfaced through the toolbar Execute button (which relabels to "Selection"), and the cursor statement gets two small gutter icon buttons next to the line numbers. Re-enable the floating bar from Settings → UI if you prefer the old behaviour.' },
    ],
  },
  {
    version: '1.4.3',
    date: '2026-04-24',
    highlights: 'Vertical tabs and a collapsible results pane',
    changes: [
      { category: 'feature', description: 'Vertical tabs — optional vertical layout for the Workspace tab bar. Toggle via the new dropdown on the tab bar or Settings → UI. Orientation and column width persist across sessions.' },
      { category: 'feature', description: 'Collapse the Results pane — hide the Results / Messages / Plan / Chart pane to give the editor full height. Use the chevron on the right side of the results tab bar (next to the zoom controls), Cmd/Ctrl+J, or "Toggle Results Pane" in the Command Palette. Each query tab remembers its own collapsed state.' },
      { category: 'improvement', description: 'Inline toolbar — shows on any selection. The floating Execute / Peek buttons now appear for arbitrary text selections, not just detected statements.' },
      { category: 'improvement', description: 'External file-change detection — open query files are now watched on disk. When a file is modified outside Jam SQL Studio, the editor picks up the change and offers to reload.' },
    ],
  },
  {
    version: '1.4.2',
    date: '2026-04-21',
    highlights: 'Inline Query Toolbar and Object Explorer schema grouping',
    changes: [
      { category: 'feature', description: 'Inline Query Toolbar — floating Execute and Peek buttons appear next to the statement under the cursor and on text selections, with smart placement that avoids overlapping code. Toggle via the Query Editor options menu ("Inline toolbar on cursor").' },
      { category: 'feature', description: 'Object Explorer — Group by schema. When a database has multiple schemas, schemas now appear as top-level folders in the sidebar with Tables, Views, and Programmability nested inside each one. Kicks in automatically for databases with more than 1 schema and more than 15 objects; switch between Auto / Always / Never in Settings → UI → Object Explorer.' },
      { category: 'improvement', description: 'Query Editor — Configurable tab size. Pick 2, 4, or 8 spaces in Settings → UI → Editor; applies to the Tab key in all query tabs.' },
      { category: 'fix', description: 'Status bar — query execution time now refreshes and settles correctly in the status bar when a query finishes.' },
      { category: 'fix', description: 'IntelliSense / auto-complete — assorted fixes for SQL completion accuracy and stability.' },
    ],
  },
  {
    version: '1.4.1',
    date: '2026-04-15',
    highlights: 'Large query results — stream millions of rows without hitting the wall',
    changes: [
      { category: 'feature', description: 'Large query results — the 50K row cap is gone; stream millions of rows with windowed virtualization, a live population progress bar, and cancel support' },
      { category: 'feature', description: 'Streaming CSV export — send multi-million row result sets straight to disk with progress tracking' },
      { category: 'improvement', description: 'Server-side sort and filter on windowed results, full-fidelity clipboard copy, and pin-as-clone for large result tabs' },
      { category: 'fix', description: 'Chart rendering fix' },
    ],
  },
  {
    version: '1.4.0',
    date: '2026-04-05',
    highlights: 'Oracle PL/SQL Debugger, SQL Server Agent Jobs Manager, and Query Editor power features',
    changes: [
      { category: 'feature', description: 'Oracle PL/SQL Debugger — step-through debugging for procedures, functions, packages, triggers, and anonymous blocks with breakpoints, variable inspection, call stack, watch expressions, and AI Chat integration (first full PL/SQL debugger on macOS and Linux)' },
      { category: 'feature', description: 'SQL Server Agent Jobs Manager — browse, run, stop, create, and edit jobs with dashboard, step editor, schedule builder, plus Operators, Alerts, Proxies, and Error Logs management' },
      { category: 'improvement', description: 'Column Profiling — null %, distinct count, min/max, avg in results grid headers' },
      { category: 'improvement', description: 'Peek Results — preview SELECT results in a floating panel' },
      { category: 'improvement', description: 'FK navigation in query results — click foreign key values to jump to referenced rows' },
      { category: 'improvement', description: 'Connection Color Theming — color-code connections to distinguish prod/staging at a glance' },
      { category: 'improvement', description: 'Column Inlay Hints — ghost column names in INSERT VALUES and SELECT * expansion' },
      { category: 'improvement', description: 'Client-Side Result Filtering — filter row for instant search without re-executing' },
      { category: 'improvement', description: 'Copy/Export as INSERT — engine-aware INSERT statements for MSSQL, MySQL, Oracle, PostgreSQL' },
      { category: 'improvement', description: 'Quick WHERE Builder — right-click a cell to append it as a WHERE condition' },
      { category: 'improvement', description: 'Pin Results — pin result sets so they survive subsequent query runs' },
      { category: 'improvement', description: 'Smart Query History Search — full-text search with filters (Ctrl/Cmd+Shift+Y)' },
      { category: 'improvement', description: 'Function Parameter Hints — signature help for function/procedure calls across all engines' },
      { category: 'improvement', description: 'Package Explorer (Oracle) — browse, search, pin, and edit PL/SQL packages across schemas' },
      { category: 'improvement', description: 'Cell aggregate bar, zoom controls, and column pinning in the results grid' },
      { category: 'improvement', description: 'F5 shortcut executes the current query; Oracle packages searchable in command palette' },
    ],
  },
  {
    version: '1.3.2',
    date: '2026-03-19',
    highlights: 'AI Agent Skill Support — one command to connect any AI agent',
    changes: [
      { category: 'feature', description: 'AI Agent Skill — run `jam-sql add-skill` to teach Claude Code, Cursor, Copilot, Codex, Windsurf, and 20+ other AI agents how to use Jam SQL Studio' },
      { category: 'feature', description: 'CLI pairing — `jam-sql pair` securely connects the CLI to the running app via challenge-response' },
      { category: 'feature', description: 'CLI auto-install — the app bundles the jam-sql CLI and installs it to PATH on every launch' },
      { category: 'feature', description: 'App launch from CLI — `jam-sql launch` starts Jam SQL Studio if not running; `jam-sql pair` offers to launch automatically' },
      { category: 'feature', description: 'App status endpoint — CLI detects blocking dialogs and guides the user to dismiss them' },
      { category: 'improvement', description: 'MCP discovery file written on every app start so the CLI always knows the app is running' },
    ],
  },
  {
    version: '1.3.1',
    date: '2026-03-18',
    changes: [
      { category: 'fix', description: 'Fixed SQL Tools Service download failing behind corporate proxies and firewalls (curl error 35) by adding TLS revocation check bypass and Electron net module fallback' },
    ],
  },
  {
    version: '1.3.0',
    date: '2026-03-15',
    highlights: 'Oracle Database Support, Command Palette, Multi-Engine DBA Tools',
    changes: [
      { category: 'feature', description: 'Oracle Database support - connect to Oracle databases with full Object Explorer, autocomplete, scripting, editable query results, and Security Manager' },
      { category: 'feature', description: 'Command Palette - quickly search database objects, switch connections, and navigate workspaces with keyboard shortcuts (Ctrl/Cmd+P)' },
      { category: 'feature', description: 'Session Browser - monitor active sessions, view locks and wait events, and kill runaway queries across Oracle, SQL Server, PostgreSQL, and MySQL' },
      { category: 'feature', description: 'Performance Dashboard - real-time performance counters, top SQL, wait statistics, memory usage, and error logs for all four engines' },
      { category: 'feature', description: 'Tablespace Manager - Oracle tablespace usage visualization, datafile details, I/O statistics, and undo monitoring' },
      { category: 'feature', description: 'Filegroup Manager - SQL Server filegroup layout, database file details, I/O statistics, and ALTER DATABASE script generation' },
    ],
  },
  {
    version: '1.2.3',
    date: '2026-03-02',
    changes: [
      { category: 'fix', description: 'Fixed Codex CLI finding issues' },
    ],
  },
  {
    version: '1.2.2',
    date: '2026-02-26',
    highlights: 'Table sub-objects in Object Explorer, connection & performance fixes',
    changes: [
      { category: 'feature', description: 'Object Explorer now shows Keys, Indexes, Constraints, Statistics, and Triggers under each table with Script as DDL support' },
      { category: 'improvement', description: 'Switching connections preserves the active database when a database with the same name exists on the new server' },
      { category: 'fix', description: 'Fixed query editor excessive CPU usage when no results are present' },
    ],
  },
  {
    version: '1.2.1',
    date: '2026-02-23',
    highlights: 'Codex CLI integration, Charts in Notebooks',
    changes: [
      { category: 'feature', description: 'Codex CLI integration for AI Chat' },
      { category: 'feature', description: 'Charts in SQL Notebooks - visualize query results with interactive charts' },
      { category: 'improvement', description: 'Charting enhancements across Query Editor and Notebooks' },
      { category: 'improvement', description: 'Copy object name from context menu in Object Explorer' },
      { category: 'fix', description: 'PostgreSQL autocomplete fix' },
      { category: 'fix', description: 'MS SQL IntelliSense restore fix' },
    ],
  },
  {
    version: '1.2.0',
    date: '2026-02-12',
    highlights: 'SQL Notebooks, AI Chat',
    changes: [
      { category: 'feature', description: 'SQL Notebooks - interactive notebooks with SQL cells, Markdown, inline results, and .ipynb import from Azure Data Studio' },
      { category: 'feature', description: 'AI Chat - conversational AI sidebar powered by Claude Code with full database context and @ mentions' },
    ],
  },
  {
    version: '1.1.1',
    date: '2026-02-11',
    changes: [
      { category: 'fix', description: 'Fixed drag & drop of SQL files not working (file path not readable)' },
    ],
  },
  {
    version: '1.1.0',
    date: '2026-02-10',
    highlights: 'MySQL support, Editable Query Results, Transaction Management, Clone Database',
    changes: [
      { category: 'feature', description: 'MySQL support - connect to MySQL databases with full autocomplete, schema overview, and browser mode' },
      { category: 'feature', description: 'Editable Query Results - edit result values directly in the query editor grid' },
      { category: 'feature', description: 'Transaction Management - explicit transaction control (BEGIN, COMMIT, ROLLBACK) in the query editor' },
      { category: 'feature', description: 'Script Management - manage and organize SQL scripts' },
      { category: 'feature', description: 'Clone Database - clone an existing database with schema and data' },
      { category: 'feature', description: 'File Drag & Drop - drag and drop SQL files to open them' },
      { category: 'improvement', description: 'PostgreSQL and MySQL multiple resultset handling' },
      { category: 'improvement', description: 'New connections appear at the top of the list' },
      { category: 'fix', description: 'Query editor performance improvements' },
      { category: 'fix', description: 'Fixed join results displaying incorrectly when columns share the same name' },
      { category: 'fix', description: 'Table Explorer and Expanded Value Dialog icon fixes' },
    ],
  },
  {
    version: '1.0.8',
    date: '2026-02-06',
    highlights: 'What\'s New dialog and Expanded Value Dialog',
    changes: [
      { category: 'feature', description: 'What\'s New dialog - see recent changes after each update' },
      { category: 'feature', description: 'Expanded Value Dialog - view and edit JSON columns with formatted syntax highlighting or multi-line plain text' },
    ],
  },
];

/** Simple semver compare: returns -1, 0, or 1 */
function compareSemver(a: string, b: string): number {
  const partsA = a.split('.').map(Number);
  const partsB = b.split('.').map(Number);
  const len = Math.max(partsA.length, partsB.length);

  for (let i = 0; i < len; i++) {
    const numA = partsA[i] ?? 0;
    const numB = partsB[i] ?? 0;
    if (numA < numB) return -1;
    if (numA > numB) return 1;
  }

  return 0;
}

/** Returns changelog entries newer than the given version */
export function getChangesSinceVersion(lastSeen: string): ChangelogEntry[] {
  return changelog.filter(
    (entry) => compareSemver(entry.version, lastSeen) > 0
  );
}
